import { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FundCall {
  id: string;
  name: string;
  call_date: string;
  requested_amount: number;
  received_amount: number;
  usage_description: string;
  notes: string;
}

interface EditFundCallModalProps {
  fundCall: FundCall;
  subsidyName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditFundCallModal({ fundCall, subsidyName, onClose, onSuccess }: EditFundCallModalProps) {
  const [formData, setFormData] = useState({
    name: fundCall.name,
    call_date: fundCall.call_date || '',
    requested_amount: fundCall.requested_amount || 0,
    received_amount: fundCall.received_amount || 0,
    usage_description: fundCall.usage_description || '',
    notes: fundCall.notes || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('grant_fund_calls')
        .update({
          name: formData.name,
          call_date: formData.call_date || null,
          requested_amount: formData.requested_amount,
          received_amount: formData.received_amount || null,
          usage_description: formData.usage_description || null,
          notes: formData.notes || null,
        })
        .eq('id', fundCall.id);

      if (error) throw error;
      onSuccess();
    } catch (error) {
      console.error('Error updating fund call:', error);
      alert("Erreur lors de la modification de l'appel de fonds");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Modifier l'appel de fonds</h2>
            <p className="text-sm text-gray-600 mt-1">{subsidyName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'appel de fonds *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: ADF 1 - Septembre 2024"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de l'appel de fonds
            </label>
            <input
              type="date"
              value={formData.call_date}
              onChange={(e) => setFormData({ ...formData, call_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant demandé (€) *
              </label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.requested_amount}
                onChange={(e) => setFormData({ ...formData, requested_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant versé (€)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.received_amount}
                onChange={(e) => setFormData({ ...formData, received_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description de l'usage
            </label>
            <textarea
              value={formData.usage_description}
              onChange={(e) => setFormData({ ...formData, usage_description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="À quoi servent ces fonds..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes internes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Notes additionnelles..."
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            onClick={handleSubmit}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
